import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import ProductSelector from './ProductSelector';
import ClientSelector from './ClientSelector';

const QuotationForm = ({ quotation, onSave, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    number: '',
    clientId: '',
    clientName: '',
    date: new Date().toISOString().split('T')[0],
    expiryDate: '',
    items: [],
    notes: '',
    discount: 0,
    taxRate: 21, // IVA 21%
    status: 'draft'
  });

  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (quotation) {
      setFormData({
        ...quotation,
        date: quotation.date ? new Date(quotation.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        expiryDate: quotation.expiryDate ? new Date(quotation.expiryDate).toISOString().split('T')[0] : ''
      });
    } else {
      // Generate new quotation number
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      
      setFormData(prev => ({
        ...prev,
        number: `PRE-${year}${month}-${randomNum}`
      }));
    }
  }, [quotation]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleClientSelect = (client) => {
    setFormData(prev => ({
      ...prev,
      clientId: client.id,
      clientName: client.name
    }));
    setShowClientSelector(false);
  };

  const handleProductAdd = (product, quantity) => {
    const newItem = {
      id: Date.now(),
      productId: product.id,
      productName: product.name,
      productCode: product.code,
      quantity: quantity,
      unitPrice: product.price,
      total: quantity * product.price
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setShowProductSelector(false);
  };

  const handleItemUpdate = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const handleItemRemove = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    const discountAmount = subtotal * (formData.discount / 100);
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = taxableAmount * (formData.taxRate / 100);
    const total = taxableAmount + taxAmount;

    return {
      subtotal,
      discountAmount,
      taxableAmount,
      taxAmount,
      total
    };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.clientName.trim()) {
      newErrors.client = 'Seleccione un cliente';
    }

    if (!formData.date) {
      newErrors.date = 'La fecha es obligatoria';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Agregue al menos un producto';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (status = 'draft') => {
    if (!validateForm()) return;

    const totals = calculateTotals();
    const quotationData = {
      ...formData,
      status,
      ...totals,
      updatedAt: new Date().toISOString()
    };

    onSave(quotationData);
  };

  const totals = calculateTotals();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="bg-surface rounded-lg border border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading font-semibold text-text-primary">
            {isEditing ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
          </h2>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onCancel}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Número de Presupuesto
              </label>
              <Input
                type="text"
                value={formData.number}
                onChange={(e) => handleInputChange('number', e.target.value)}
                placeholder="PRE-2024-001"
                disabled={isEditing}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Fecha
              </label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
              />
              {errors.date && (
                <p className="text-error text-xs mt-1">{errors.date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Cliente *
              </label>
              <div className="flex space-x-2">
                <Input
                  type="text"
                  value={formData.clientName}
                  placeholder="Seleccionar cliente..."
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="md"
                  iconName="Search"
                  onClick={() => setShowClientSelector(true)}
                />
              </div>
              {errors.client && (
                <p className="text-error text-xs mt-1">{errors.client}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Fecha de Vencimiento
              </label>
              <Input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', e.target.value)}
              />
            </div>
          </div>

          {/* Products Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-medium text-text-primary">
                Productos
              </h3>
              <Button
                variant="primary"
                size="sm"
                iconName="Plus"
                onClick={() => setShowProductSelector(true)}
              >
                Agregar Producto
              </Button>
            </div>

            {errors.items && (
              <p className="text-error text-sm mb-4">{errors.items}</p>
            )}

            {formData.items.length === 0 ? (
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Icon name="Package" size={48} className="text-text-muted mx-auto mb-4" />
                <p className="text-text-muted">
                  No hay productos agregados
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  onClick={() => setShowProductSelector(true)}
                  className="mt-4"
                >
                  Agregar Primer Producto
                </Button>
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-secondary-50 px-4 py-3 border-b border-border">
                  <div className="grid grid-cols-12 gap-4 text-sm font-medium text-text-primary">
                    <div className="col-span-4">Producto</div>
                    <div className="col-span-2 text-center">Cantidad</div>
                    <div className="col-span-2 text-right">Precio Unit.</div>
                    <div className="col-span-2 text-right">Total</div>
                    <div className="col-span-2 text-center">Acciones</div>
                  </div>
                </div>
                
                <div className="divide-y divide-border">
                  {formData.items.map((item) => (
                    <div key={item.id} className="px-4 py-3">
                      <div className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-4">
                          <div className="text-sm font-medium text-text-primary">
                            {item.productName}
                          </div>
                          <div className="text-xs text-text-muted">
                            Código: {item.productCode}
                          </div>
                        </div>
                        
                        <div className="col-span-2">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemUpdate(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="1"
                            className="text-center"
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => handleItemUpdate(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            className="text-right"
                          />
                        </div>
                        
                        <div className="col-span-2 text-right text-sm font-medium text-text-primary">
                          {formatCurrency(item.total)}
                        </div>
                        
                        <div className="col-span-2 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => handleItemRemove(item.id)}
                            className="text-error hover:text-error hover:bg-error-50"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Totals and Additional Info */}
          {formData.items.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Notes and Discount */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Descuento (%)
                  </label>
                  <Input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => handleInputChange('discount', parseFloat(e.target.value) || 0)}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Notas
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Notas adicionales..."
                    rows={4}
                    className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>
              </div>

              {/* Totals */}
              <div className="bg-secondary-50 p-4 rounded-lg">
                <h4 className="font-medium text-text-primary mb-4">Resumen</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Subtotal:</span>
                    <span className="text-text-primary">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  
                  {formData.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Descuento ({formData.discount}%):</span>
                      <span className="text-error">-{formatCurrency(totals.discountAmount)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Base Imponible:</span>
                    <span className="text-text-primary">{formatCurrency(totals.taxableAmount)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-text-secondary">IVA ({formData.taxRate}%):</span>
                    <span className="text-text-primary">{formatCurrency(totals.taxAmount)}</span>
                  </div>
                  
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between font-medium text-lg">
                      <span className="text-text-primary">Total:</span>
                      <span className="text-primary">{formatCurrency(totals.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-border bg-secondary-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-muted">
            {isEditing ? 'Editando presupuesto' : 'Nuevo presupuesto'}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => handleSave('draft')}
              iconName="Save"
            >
              Guardar Borrador
            </Button>
            
            <Button
              variant="primary"
              onClick={() => handleSave('sent')}
              iconName="Send"
            >
              Enviar Presupuesto
            </Button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showClientSelector && (
        <ClientSelector
          onSelect={handleClientSelect}
          onClose={() => setShowClientSelector(false)}
        />
      )}
      
      {showProductSelector && (
        <ProductSelector
          onAdd={handleProductAdd}
          onClose={() => setShowProductSelector(false)}
        />
      )}
    </div>
  );
};

export default QuotationForm;